// Phase 4.5 — Interior CMS API clients.
//
// Four resources: designers, packages, portfolio, page content.
// All call /v1/<resource> — the global `api` helper auto-prefixes /v1/,
// so BASE values here MUST NOT include /v1/ (otherwise we get /v1/v1/).
//
// Each client exposes toUi/toApi adapters so views can work with friendly
// camelCase JS objects while sending the right shape to the API.

import { api } from "./api";

// ─── Helpers ──────────────────────────────────────────────────────────

function buildQs(params = {}) {
  const usp = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== "") usp.append(k, String(v));
  });
  const s = usp.toString();
  return s ? "?" + s : "";
}

// ═══════════════════════════════════════════════════════════════════
//  Designers
// ═══════════════════════════════════════════════════════════════════

const DESIGNERS_BASE = "/interior-designers";

function designerToUi(r) {
  if (!r) return null;
  return {
    id: r.id,
    slug: r.slug,
    name: r.name,
    role: r.role || "",
    bio: r.bio || "",
    photoUrl: r.photoUrl || null,
    yearsExperience: r.yearsExperience ?? null,
    specialties: Array.isArray(r.specialties) ? r.specialties : [],
    instagramUrl: r.instagramUrl || "",
    email: r.email || "",
    phone: r.phone || "",
    active: r.active !== false,
    featured: !!r.featured,
    sortOrder: r.sortOrder ?? 0,
    isDemo: !!r.isDemo,
    createdAt: r.createdAt,
    updatedAt: r.updatedAt,
  };
}

function designerToApi(ui, isUpdate = false) {
  const out = {};
  if (ui.name !== undefined) out.name = ui.name;
  if (ui.slug !== undefined && ui.slug !== "") out.slug = ui.slug;
  if (ui.role !== undefined) out.role = ui.role || null;
  if (ui.bio !== undefined) out.bio = ui.bio || null;
  if (ui.photoUrl !== undefined) out.photoUrl = ui.photoUrl || null;
  if (ui.yearsExperience !== undefined) {
    out.yearsExperience = ui.yearsExperience === "" || ui.yearsExperience === null
      ? null : Number(ui.yearsExperience);
  }
  if (ui.specialties !== undefined) out.specialties = Array.isArray(ui.specialties) ? ui.specialties : [];
  if (ui.instagramUrl !== undefined) out.instagramUrl = ui.instagramUrl || null;
  if (ui.email !== undefined) out.email = ui.email || null;
  if (ui.phone !== undefined) out.phone = ui.phone || null;
  if (ui.active !== undefined) out.active = !!ui.active;
  if (ui.featured !== undefined) out.featured = !!ui.featured;
  if (ui.sortOrder !== undefined) out.sortOrder = Number(ui.sortOrder) || 0;
  return out;
}

export const designersApi = {
  async list(opts = {}) {
    const qs = buildQs({ includeInactive: opts.includeInactive ? "true" : undefined });
    const r = await api.get(`${DESIGNERS_BASE}${qs}`);
    return { items: (r.items || []).map(designerToUi), total: r.total ?? 0 };
  },
  async getOne(id) {
    const r = await api.get(`${DESIGNERS_BASE}/${encodeURIComponent(id)}`);
    return designerToUi(r);
  },
  async create(ui) {
    const r = await api.post(DESIGNERS_BASE, designerToApi(ui));
    return designerToUi(r);
  },
  async update(id, ui) {
    const r = await api.patch(`${DESIGNERS_BASE}/${encodeURIComponent(id)}`, designerToApi(ui, true));
    return designerToUi(r);
  },
  async delete(id) {
    return api.delete(`${DESIGNERS_BASE}/${encodeURIComponent(id)}`);
  },
};

// ═══════════════════════════════════════════════════════════════════
//  Packages
// ═══════════════════════════════════════════════════════════════════

const PACKAGES_BASE = "/interior-packages";

function packageToUi(r) {
  if (!r) return null;
  return {
    id: r.id,
    slug: r.slug,
    name: r.name,
    tagline: r.tagline || "",
    description: r.description || "",
    basePriceDisplay: r.basePriceDisplay || "",
    basePrice: r.basePrice ?? null,
    features: Array.isArray(r.features) ? r.features : [],
    targetMarket: r.targetMarket || "",
    includedServices: Array.isArray(r.includedServices) ? r.includedServices : [],
    heroImageUrl: r.heroImageUrl || "",
    gallery: Array.isArray(r.gallery) ? r.gallery : [],
    popular: !!r.popular,
    featured: !!r.featured,
    active: r.active !== false,
    sortOrder: r.sortOrder ?? 0,
    isDemo: !!r.isDemo,
    createdAt: r.createdAt,
    updatedAt: r.updatedAt,
  };
}

function packageToApi(ui) {
  const out = {};
  if (ui.name !== undefined) out.name = ui.name;
  if (ui.slug !== undefined && ui.slug !== "") out.slug = ui.slug;
  if (ui.tagline !== undefined) out.tagline = ui.tagline || null;
  if (ui.description !== undefined) out.description = ui.description || null;
  if (ui.basePriceDisplay !== undefined) out.basePriceDisplay = ui.basePriceDisplay || null;
  if (ui.basePrice !== undefined) {
    out.basePrice = ui.basePrice === "" || ui.basePrice === null
      ? null : Number(ui.basePrice);
  }
  if (ui.features !== undefined) out.features = Array.isArray(ui.features) ? ui.features.filter(Boolean) : [];
  if (ui.targetMarket !== undefined) out.targetMarket = ui.targetMarket || null;
  if (ui.includedServices !== undefined) out.includedServices = Array.isArray(ui.includedServices) ? ui.includedServices.filter(Boolean) : [];
  if (ui.heroImageUrl !== undefined) out.heroImageUrl = ui.heroImageUrl || null;
  if (ui.gallery !== undefined) out.gallery = Array.isArray(ui.gallery) ? ui.gallery : [];
  if (ui.popular !== undefined) out.popular = !!ui.popular;
  if (ui.featured !== undefined) out.featured = !!ui.featured;
  if (ui.active !== undefined) out.active = !!ui.active;
  if (ui.sortOrder !== undefined) out.sortOrder = Number(ui.sortOrder) || 0;
  return out;
}

export const packagesApi = {
  async list(opts = {}) {
    const qs = buildQs({ includeInactive: opts.includeInactive ? "true" : undefined });
    const r = await api.get(`${PACKAGES_BASE}${qs}`);
    return { items: (r.items || []).map(packageToUi), total: r.total ?? 0 };
  },
  async getOne(id) {
    const r = await api.get(`${PACKAGES_BASE}/${encodeURIComponent(id)}`);
    return packageToUi(r);
  },
  async create(ui) {
    const r = await api.post(PACKAGES_BASE, packageToApi(ui));
    return packageToUi(r);
  },
  async update(id, ui) {
    const r = await api.patch(`${PACKAGES_BASE}/${encodeURIComponent(id)}`, packageToApi(ui));
    return packageToUi(r);
  },
  async delete(id) {
    return api.delete(`${PACKAGES_BASE}/${encodeURIComponent(id)}`);
  },
};

// ═══════════════════════════════════════════════════════════════════
//  Portfolio
// ═══════════════════════════════════════════════════════════════════

const PORTFOLIO_BASE = "/interior-portfolio";

function portfolioToUi(r) {
  if (!r) return null;
  return {
    id: r.id,
    slug: r.slug,
    title: r.title,
    category: r.category || "",
    location: r.location || "",
    propertyType: r.propertyType || "",
    description: r.description || "",
    heroImageUrl: r.heroImageUrl || "",
    beforeImages: Array.isArray(r.beforeImages) ? r.beforeImages : [],
    afterImages: Array.isArray(r.afterImages) ? r.afterImages : [],
    galleryImages: Array.isArray(r.galleryImages) ? r.galleryImages : [],
    tags: Array.isArray(r.tags) ? r.tags : [],
    packageId: r.packageId || null,
    package: r.package || null,
    designerId: r.designerId || null,
    designer: r.designer || null,
    completedAt: r.completedAt || null,
    active: r.active !== false,
    featured: !!r.featured,
    sortOrder: r.sortOrder ?? 0,
    isDemo: !!r.isDemo,
    createdAt: r.createdAt,
    updatedAt: r.updatedAt,
  };
}

function portfolioToApi(ui) {
  const out = {};
  if (ui.title !== undefined) out.title = ui.title;
  if (ui.slug !== undefined && ui.slug !== "") out.slug = ui.slug;
  if (ui.category !== undefined) out.category = ui.category || null;
  if (ui.location !== undefined) out.location = ui.location || null;
  if (ui.propertyType !== undefined) out.propertyType = ui.propertyType || null;
  if (ui.description !== undefined) out.description = ui.description || null;
  if (ui.heroImageUrl !== undefined) out.heroImageUrl = ui.heroImageUrl || null;
  if (ui.beforeImages !== undefined) out.beforeImages = Array.isArray(ui.beforeImages) ? ui.beforeImages : [];
  if (ui.afterImages !== undefined) out.afterImages = Array.isArray(ui.afterImages) ? ui.afterImages : [];
  if (ui.galleryImages !== undefined) out.galleryImages = Array.isArray(ui.galleryImages) ? ui.galleryImages : [];
  if (ui.tags !== undefined) out.tags = Array.isArray(ui.tags) ? ui.tags.filter(Boolean) : [];
  if (ui.packageId !== undefined) out.packageId = ui.packageId || null;
  if (ui.designerId !== undefined) out.designerId = ui.designerId || null;
  if (ui.completedAt !== undefined) out.completedAt = ui.completedAt || null;
  if (ui.active !== undefined) out.active = !!ui.active;
  if (ui.featured !== undefined) out.featured = !!ui.featured;
  if (ui.sortOrder !== undefined) out.sortOrder = Number(ui.sortOrder) || 0;
  return out;
}

export const portfolioApi = {
  async list(opts = {}) {
    const qs = buildQs({
      includeInactive: opts.includeInactive ? "true" : undefined,
      category: opts.category,
      packageId: opts.packageId,
      designerId: opts.designerId,
      featuredOnly: opts.featuredOnly ? "true" : undefined,
      page: opts.page,
      pageSize: opts.pageSize,
    });
    const r = await api.get(`${PORTFOLIO_BASE}${qs}`);
    return {
      items: (r.items || []).map(portfolioToUi),
      total: r.total ?? 0,
      page: r.page ?? 1,
      pageSize: r.pageSize ?? 50,
    };
  },
  async getOne(id) {
    const r = await api.get(`${PORTFOLIO_BASE}/${encodeURIComponent(id)}`);
    return portfolioToUi(r);
  },
  async create(ui) {
    const r = await api.post(PORTFOLIO_BASE, portfolioToApi(ui));
    return portfolioToUi(r);
  },
  async update(id, ui) {
    const r = await api.patch(`${PORTFOLIO_BASE}/${encodeURIComponent(id)}`, portfolioToApi(ui));
    return portfolioToUi(r);
  },
  async delete(id) {
    return api.delete(`${PORTFOLIO_BASE}/${encodeURIComponent(id)}`);
  },
};

// ═══════════════════════════════════════════════════════════════════
//  Page Content (singleton)
// ═══════════════════════════════════════════════════════════════════

const PAGE_CONTENT_BASE = "/interior-page-content";

function pageContentToUi(r) {
  if (!r) return null;
  return {
    id: r.id,
    heroTitle: r.heroTitle || "",
    heroSubtitle: r.heroSubtitle || "",
    heroImageUrl: r.heroImageUrl || "",
    services: Array.isArray(r.services) ? r.services : [],
    categories: Array.isArray(r.categories) ? r.categories : [],
    trust: Array.isArray(r.trust) ? r.trust : [],
    aboutTitle: r.aboutTitle || "",
    aboutBody: r.aboutBody || "",
    usps: Array.isArray(r.usps) ? r.usps : [],
    processSteps: Array.isArray(r.processSteps) ? r.processSteps : [],
    faqs: Array.isArray(r.faqs) ? r.faqs : [],
    ctaTitle: r.ctaTitle || "",
    ctaSubtitle: r.ctaSubtitle || "",
    advisorName: r.advisorName || "",
    advisorInitials: r.advisorInitials || "",
    advisorRole: r.advisorRole || "",
    advisorRating: r.advisorRating ?? null,
    updatedAt: r.updatedAt,
  };
}

function pageContentToApi(ui) {
  const out = {};
  const stringFields = [
    "heroTitle", "heroSubtitle", "heroImageUrl",
    "aboutTitle", "aboutBody",
    "ctaTitle", "ctaSubtitle",
    "advisorName", "advisorInitials", "advisorRole",
  ];
  for (const f of stringFields) {
    if (ui[f] !== undefined) out[f] = ui[f] || null;
  }
  const arrayFields = ["services", "categories", "trust", "usps", "processSteps", "faqs"];
  for (const f of arrayFields) {
    if (ui[f] !== undefined) out[f] = Array.isArray(ui[f]) ? ui[f] : [];
  }
  if (ui.advisorRating !== undefined) {
    out.advisorRating = ui.advisorRating === "" || ui.advisorRating === null
      ? null : Number(ui.advisorRating);
  }
  return out;
}

export const pageContentApi = {
  async get() {
    const r = await api.get(PAGE_CONTENT_BASE);
    return pageContentToUi(r);
  },
  async update(ui) {
    const r = await api.patch(PAGE_CONTENT_BASE, pageContentToApi(ui));
    return pageContentToUi(r);
  },
};
